import { buildProps, definePropType } from '@ll-plus/utils'

import type Cropper from 'cropperjs'
import type { CSSProperties, ExtractPropTypes } from 'vue'
import { borderType } from '../../upload-image'
import type CropperImage from './cropper-image.vue'
import type CropperModal from './cropper-modal.vue'

export interface CropendResult {
  imgBase64: string
  imgInfo: Cropper.Data
}

export const defaultCropperOptions: Cropper.Options = {
  aspectRatio: 1,
  zoomable: true,
  zoomOnTouch: true,
  zoomOnWheel: true,
  cropBoxMovable: true,
  cropBoxResizable: true,
  toggleDragModeOnDblclick: true,
  autoCrop: true,
  autoCropArea: 1,
  background: true,
  highlight: true,
  center: true,
  responsive: true,
  restore: true,
  checkCrossOrigin: true,
  checkOrientation: true,
  scalable: true,
  modal: true,
  guides: true,
  movable: true,
  rotatable: true,
}

export const cropperImageProps = buildProps({
  src: {
    type: String,
    required: true,
  },
  alt: {
    type: String,
  },
  circled: {
    type: Boolean,
    default: false,
  },
  loading: {
    type: Boolean,
    default: false,
  },
  realTimePreview: {
    type: Boolean,
    default: true,
  },
  height: {
    type: [String, Number],
    default: '360px',
  },
  crossorigin: {
    type: definePropType<'' | 'anonymous' | 'use-credentials' | undefined>(
      String
    ),
    default: undefined,
  },
  imageStyle: {
    type: definePropType<CSSProperties>(Object),
    default: () => ({}),
  },
  options: {
    type: definePropType<Cropper.Options>(Object),
    default: () => ({}),
  },
} as const)

export const cropperImageEmits = {
  cropend: (params: CropendResult) => !!params,
  onReady: (cropper: Cropper) => !!cropper,
  cropendError: () => true,
} as const

export const maskMode = ['fade-in', 'default'] as const

export const cropperModalProps = buildProps({
  circled: {
    type: Boolean,
    default: true,
  },
  disabled: {
    type: Boolean,
    default: false,
  },
  loading: {
    type: Boolean,
    default: false,
  },
  avatar: {
    type: String,
    default: '',
  },
  avatarSize: {
    type: [String, Number],
    default: 64,
  },
  avatarType: {
    type: definePropType<'square' | 'circle'>(String),
    default: () => 'circle',
  },
  hideDelete: {
    type: Boolean,
    default: false,
  },
  title: {
    type: String,
    default: '图片裁剪',
  },

  // 边框形态
  borderType: {
    type: String,
    values: borderType,
    default: 'dashed',
  },

  // 图片圆角
  borderRadius: {
    type: String,
    default: '6px',
  },
  // 上传图标
  uploadIcon: {
    type: String,
    default: 'icon-add',
  },
  // 上传图标大小
  uploadIconSize: {
    type: Number,
    default: 24,
  },
  // 上传标题
  uploadTitle: {
    type: String,
    default: '',
  },
  // 上传描述
  uploadTip: {
    type: String,
    default: '',
  },

  // 蒙层展示方式
  maskMode: {
    type: String,
    values: maskMode,
    default: 'default',
  },

  // 蒙层文本
  maskText: {
    type: String,
    default: '修改',
  },

  // 蒙层高度
  maskHeight: {
    type: String,
    default: '24px',
  },

  // 蒙层高度
  maskTextSize: {
    type: String,
    default: '14px',
  },
} as const)

export const cropperModalEmits = {
  confirm: (params: { blob: Blob; previewSource: string }) => !!params,
  'update:avatar': (avatar: string) => !!avatar || typeof avatar === 'string',
} as const

// props
export type CropperImageProps = ExtractPropTypes<typeof cropperImageProps>
export type CropperModalProps = ExtractPropTypes<typeof cropperModalProps>

// emits
export type CropperImageEmits = typeof cropperImageEmits
export type CropperModalEmits = typeof cropperModalEmits

// instance
export type CropperImageInstance = InstanceType<typeof CropperImage>
export type CropperModalInstance = InstanceType<typeof CropperModal>
